import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromVehicles from '../reducers/vehicle.reducer';
import { FilterItem } from '@fc-vehicles/components/shared/vehicle-filter/vehicles-filter.component';
import {
  Hardware,
  Harness,
  Vehicle,
  VehicleGroup,
  VehicleType,
} from '@fc-core';
import {
  getCurrentDriver,
  getDriverFullName,
} from '@fc-core/models/vehicle/vehicle-driver.utils';
import {
  getAllVehicleGroups,
  getSelectedFilterGroup,
  getSelectedGroup,
} from './vehicle-group.selectors';
import { Dictionary } from '@ngrx/entity';
import {
  getSearchFields,
  getSearchText,
  getSelectedFilters,
  getVehicleSortBy,
} from './vehicle-filters.selectors';
import moment from 'moment';
import { SearchBy } from '../reducers/vehicle-filters.reducer';
import { SortBy } from '@fc-shared/ui/sort-by/sort-by';
import { sortByPropertyWithOrder } from '@fc-shared/utils/sort-by-property';
import { selectRouterParam } from '@fc-store/selectors/router.selectors';
import { getAllVehicleHardware } from '@fc-store/selectors/hardware.selectors';
import { getAllHarnessList } from '@fc-store/selectors/harness.selectors';
import * as fromVehicle from '@fc-store/reducers/vehicle.reducer';

const getAllVehiclesGroup = (vehicles: Vehicle[]) => {
  return {
    id: -1,
    name: `All vehicles (${vehicles?.length})`,
    color: '#B0BEC5',
  };
};

export const getCoreVehicleState = createFeatureSelector<fromVehicle.State>(
  fromVehicle.vehiclesFeatureKey,
);

export const getAllVehicleEntities = createSelector(
  getCoreVehicleState,
  fromVehicles.selectEntities,
);

export const getAllRawVehicles = createSelector(
  getCoreVehicleState,
  fromVehicles.selectAll,
);

export const getVehicleTypes = createSelector(
  getCoreVehicleState,
  fromVehicles.getVehicleTypes,
);

export const getAllVehicleFromStore = createSelector(
  getAllRawVehicles,
  getAllVehicleGroups,
  getVehicleTypes,
  (
    vehicles: Vehicle[],
    vehicleGroups: VehicleGroup[],
    vehicleTypes: VehicleType[],
  ) => {
    return vehicles?.map((vehicle) => {
      return {
        ...vehicle,
        typeObj: vehicleTypes?.find((type) => type.id === vehicle.type),
        operatorName: vehicle.operators?.length
          ? vehicle.operators[0]?.employee?.firstName +
            ' ' +
            vehicle.operators[0]?.employee?.lastName
          : undefined,
        groups: getVehicleGroupIds(vehicleGroups, vehicle.id),
      };
    });
  },
);

export const getSetLocationLoading = createSelector(
  getCoreVehicleState,
  fromVehicles.getSetLocationLoading,
);

export const getCoreVehicleError = createSelector(
  getCoreVehicleState,
  fromVehicles.getVehicleError,
);

export const getUsedVehicleTypes = createSelector(
  getCoreVehicleState,
  fromVehicles.getUsedVehicleTypes,
);

export const getUsedVehicleTypeById = (id: number) =>
  createSelector(getUsedVehicleTypes, (vehicleTypes) =>
    vehicleTypes.find((type) => type.id === id),
  );

export const getAllFiltersChanges = createSelector(
  getSearchText,
  getSelectedFilters,
  getUsedVehicleTypes,
  getSearchFields,
  getSelectedFilterGroup,
  (
    searchText: string,
    vehicleFilters: FilterItem[],
    vehicleTypes: VehicleType[],
    searchFields: SearchBy[],
    group: SelectedVehicleGroup,
  ) => {
    return { searchText, vehicleFilters, vehicleTypes, searchFields, group };
  },
);

// all vehicles with all filter but without selected group filter
export const selectAllVehiclesWithoutGroupFilter = createSelector(
  getAllVehicleFromStore,
  getSearchText,
  getVehicleSortBy,
  getSelectedFilters,
  getUsedVehicleTypes,
  getSearchFields,
  (
    vehicles: Vehicle[],
    searchText: string,
    sortBy: SortBy,
    vehicleFilters: FilterItem[],
    vehicleTypes: VehicleType[],
    searchFields: SearchBy[],
  ) => {
    vehicles = vehicleSearchFilter(
      vehicles,
      searchText,
      vehicleTypes,
      searchFields,
    );
    vehicles = sortVehicles(vehicles, sortBy);
    vehicles = applySelectedVehicleFilters(vehicles, vehicleFilters);
    return vehicles;
  },
);

// all vehicles with all filter and selected group filter applied
export const selectAllVehicles = createSelector(
  selectAllVehiclesWithoutGroupFilter,
  getSelectedFilterGroup,
  (vehicles: Vehicle[], group: SelectedVehicleGroup) => {
    if (!group) return vehicles;
    return filterVehicleBySelectedGroup(vehicles, group);
  },
);

export const getVehiclesForGroupTable = createSelector(
  selectAllVehiclesWithoutGroupFilter,
  getSelectedGroup,
  (vehicles: Vehicle[], group: ReturnType<typeof getSelectedGroup>) => {
    if (!group) return [];
    return filterVehicleBySelectedGroup(vehicles, group);
  },
);

// todo we should get count from backend
// to show all groups with vehicles count
export const getAllVehicleGroupsWithCount = createSelector(
  selectAllVehiclesWithoutGroupFilter,
  getAllVehicleGroups,
  (vehicles: Vehicle[], groups: VehicleGroup[]) => {
    const allVehiclesGroup = getAllVehiclesGroup(vehicles);
    if (!groups || groups?.length === 0) return [allVehiclesGroup];
    const allGroups = groups?.map((group) => ({
      ...group,
      name: `${group.name} (${countVehiclesInGroup(vehicles, group)})`,
    }));
    return [allVehiclesGroup, ...allGroups];
  },
);

export const getSelectedVehicleGroupWithCount = createSelector(
  getSelectedFilterGroup,
  selectAllVehiclesWithoutGroupFilter,
  (group: SelectedVehicleGroup, vehicles: Vehicle[]) => {
    return !group || group.id === -1
      ? getAllVehiclesGroup(vehicles)
      : {
          ...group,
          name: `${group.name} (${countVehiclesInGroup(vehicles, group)})`,
        };
  },
);

export const getSelectedVehicleGroupWithCountById = (id: number) =>
  createSelector(getAllVehicleGroupsWithCount, (groups) =>
    groups.find((group) => group.id === id),
  );

export const getLoadedFields = createSelector(
  getCoreVehicleState,
  fromVehicles.getLoadedFields,
);
export const getLoadedState = createSelector(
  getCoreVehicleState,
  fromVehicles.getLoadedState,
);
export const getLoadingState = createSelector(
  getCoreVehicleState,
  fromVehicles.getLoadingState,
);
export const getCount = createSelector(
  getCoreVehicleState,
  fromVehicles.getCount,
);

export const getSelectedVehicleId = createSelector(
  getCoreVehicleState,
  fromVehicles.getSelectedVehicleId,
);
export const getProgress = createSelector(
  getAllRawVehicles,
  getCount,
  getLoadingState,
  (allVehicles: Vehicle[], count: number, loading: boolean) => {
    if (allVehicles?.length === 0 && !loading) return 100;
    if (allVehicles?.length > 0) return (allVehicles?.length / count) * 100;
  },
);

// TODO: replace with getVehicleFromRouterId [todo-007]

export const getVehicleFromRouterId = createSelector(
  getAllVehicleFromStore,
  selectRouterParam('id'),
  (vehicles, id) => {
    return vehicles.find((vehicle) => vehicle.id === +id);
  },
);

export const getAllVehiclesWithCurrentOptions = createSelector(
  getAllVehicleFromStore,
  getLoadedFields,
  getLoadedState,
  getLoadingState,
  getCount,
  (vehicles, options, loaded, loading, count) => {
    return { vehicles, options, loaded, loading, count };
  },
);

export const getVehicleById = (id: number) => {
  if (!id) return null;
  return createSelector(
    getAllVehicleEntities,
    (vehicles: Dictionary<Vehicle>) => vehicles[id],
  );
};

export const selectVehicleByIdWithHarnessAndHardware = (id: number) => {
  return createSelector(
    getAllVehicleEntities,
    getAllVehicleGroups,
    getAllVehicleHardware,
    getAllHarnessList,
    (
      vehicles: Dictionary<Vehicle>,
      vehicleGroups: VehicleGroup[],
      hardwareList: Hardware[],
      harnessList: Harness[],
    ) => {
      const vehicle = vehicles?.[id];
      if (vehicle && hardwareList && harnessList) {
        return {
          ...vehicle,
          groups: getVehicleGroupIds(vehicleGroups, vehicle.id),
          hardwareObj: vehicle.hardware
            ? hardwareList.find((hardware) => hardware.id === vehicle.hardware)
            : null,
          harnessObj: vehicle.harness
            ? harnessList.find((harness) => harness.id === vehicle.harness)
            : null,
        } as Vehicle;
      }
      return undefined;
    },
  );
};

const filterVehicleBySelectedGroup = (
  vehicles: Vehicle[],
  group: VehicleGroup,
): Vehicle[] => {
  if (group && group.id !== -1) {
    return vehicles.filter((vehicle) => group.vehicles?.includes(vehicle.id));
  }
  return vehicles;
};

const applySelectedVehicleFilters = (
  vehicles: Vehicle[],
  vehicleFilters: FilterItem[],
): Vehicle[] => {
  if (vehicleFilters?.length) {
    const statuses = vehicleFilters.filter((i) => i.type === 'status');
    const types = vehicleFilters.filter((i) => i.type === 'type');
    if (statuses?.length) {
      const statusNames = statuses.map((i) => i.name.toLowerCase());
      vehicles = vehicles.filter(
        (vehicle) =>
          !!vehicle.lastState?.type &&
          statusNames.includes(vehicle.lastState.type),
      );
    }
    if (types?.length) {
      vehicles = vehicles.filter((vehicle) =>
        types.map((i) => i.id).includes(vehicle.type),
      );
    }
    const qrFilters = vehicleFilters.filter((i) => i.type === 'qr');
    if (qrFilters.length === 1) {
      const filterLinked = qrFilters[0].id === 'linked';
      vehicles = vehicles.filter((v) =>
        filterLinked ? v.hasQrCode : !v.hasQrCode,
      );
    }
    return vehicles;
  }
  return vehicles;
};

export const vehicleSearchFilter = (
  vehicles: Vehicle[],
  searchText: string,
  vehicleTypes: VehicleType[],
  searchFields: SearchBy[],
): Vehicle[] => {
  if (searchText?.trim().length) {
    const search = searchText?.trim()?.toLowerCase();
    return vehicles.filter((vehicle) => {
      const filters = [];
      if (searchFields?.includes('identifier'))
        filters.push(searchByIdentifier(vehicle, search));
      if (searchFields?.includes('vehicleInfo'))
        filters.push(searchByVehicleInfo(vehicle, search));
      if (searchFields?.includes('type'))
        filters.push(searchByType(vehicleTypes, vehicle, search));
      if (searchFields?.includes('operators'))
        filters.push(searchByCurrentDriver(vehicle, search));
      return filters.some((i) => i);
    });
  }
  return vehicles;
};

const searchByVehicleInfo = (vehicle: Vehicle, search: string): boolean => {
  return `${vehicle?.manufacturer?.name || ''} ${vehicle?.model || ''}`
    .toLowerCase()
    .includes(search);
};

const searchByIdentifier = (vehicle: Vehicle, search: string): boolean => {
  return vehicle.identifier.toLowerCase().includes(search);
};

export const searchByType = (
  vehicleTypes: VehicleType[],
  vehicle: Vehicle,
  search: string,
): boolean => {
  const vehicleType = vehicleTypes.find((i) => i.id === vehicle.type);
  return !!vehicleType && vehicleType.name.toLowerCase() === search;
};

type SelectedVehicleGroup = ReturnType<typeof getSelectedFilterGroup>;

const getVehicleGroupIds = (
  vehicleGroups: VehicleGroup[] | undefined,
  vehicleId: number,
): number[] =>
  (vehicleGroups ?? [])
    .filter((group) => group.vehicles?.includes(vehicleId))
    .map((group) => group.id)
    .filter((groupId): groupId is number => groupId !== undefined);

const countVehiclesInGroup = (
  vehicles: Vehicle[],
  group: VehicleGroup,
): number => {
  const groupId = group.id;
  if (groupId === undefined) return 0;
  return vehicles.filter((vehicle) => vehicle?.groups?.includes(groupId))
    .length;
};

const searchByCurrentDriver = (vehicle: Vehicle, search: string): boolean => {
  const driver = getCurrentDriver(vehicle);
  return !!driver && getDriverFullName(driver).toLowerCase().includes(search);
};

const sortVehicles = (vehicles: Vehicle[], sortBy: SortBy): Vehicle[] => {
  if (!vehicles?.length) return [];
  if (!sortBy) return vehicles;
  if (sortBy.order === null || sortBy.order === undefined) sortBy.order = false;

  switch (sortBy.value) {
    case 'identifier':
      return sortByPropertyWithOrder<Vehicle>(
        vehicles,
        'identifier',
        sortBy.order,
      );
    case 'manufacturer':
      return [...vehicles].sort((a, b) => {
        if (!a.manufacturer || !b.manufacturer) return 0;
        return sortBy.order
          ? b.manufacturer.name.localeCompare(a.manufacturer.name)
          : a.manufacturer.name.localeCompare(b.manufacturer.name);
      });
    case 'model':
      return sortByPropertyWithOrder<Vehicle>(vehicles, 'model', sortBy.order);
    case 'created':
      return [...vehicles].sort((a, b) => {
        if (!a.created || !b.created) return 0;
        return sortBy.order
          ? moment(a.created).diff(moment(b.created))
          : moment(b.created).diff(moment(a.created));
      });
    case 'last_updated':
      return [
        ...vehicles.filter((vehicle) => !vehicle.lastReading),
        ...vehicles
          .filter((vehicle) => vehicle.lastReading)
          .sort((a, b) => {
            const aReadingTime = a.lastReading?.datetime;
            const bReadingTime = b.lastReading?.datetime;
            if (!aReadingTime || !bReadingTime) return 0;
            return sortBy.order
              ? moment(bReadingTime).diff(aReadingTime)
              : moment(aReadingTime).diff(bReadingTime);
          }),
      ];
    default:
      return vehicles;
  }
};
